import { useState, useMemo } from 'react';
import { AlertCircle, Hash } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea, Input, Field } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';

export function RegexTester() {
  const [pattern, setPattern] = useState('\\b(\\w+)@([a-zA-Z0-9.-]+\\.\\w+)\\b');
  const [text, setText] = useState(
    'Reach me at hello@utility.os or support@example.com — also see notes@team.io.',
  );
  const [flags, setFlags] = useState({ g: true, i: false, m: false, s: false });

  const result = useMemo(() => {
    if (!pattern) return { matches: [], err: null as string | null };
    try {
      const flagStr = Object.entries(flags)
        .filter(([, on]) => on)
        .map(([k]) => k)
        .join('');
      const re = new RegExp(pattern, flagStr);
      const matches: { index: number; full: string; groups: string[] }[] = [];
      if (flags.g) {
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
          matches.push({ index: m.index, full: m[0], groups: m.slice(1) });
          if (m[0] === '') re.lastIndex++;
        }
      } else {
        const m = re.exec(text);
        if (m) matches.push({ index: m.index, full: m[0], groups: m.slice(1) });
      }
      return { matches, err: null };
    } catch (e) {
      return { matches: [], err: e instanceof Error ? e.message : 'Invalid pattern' };
    }
  }, [pattern, text, flags]);

  const highlighted = useMemo(() => {
    if (!result.matches.length) return text;
    let out = '';
    let cursor = 0;
    for (const m of result.matches) {
      out += escapeHtml(text.slice(cursor, m.index));
      out += `<mark class="bg-warning/30 text-fg rounded px-0.5">${escapeHtml(m.full)}</mark>`;
      cursor = m.index + m.full.length;
    }
    out += escapeHtml(text.slice(cursor));
    return out;
  }, [result.matches, text]);

  return (
    <div>
      <ToolHeader
        toolId="regex-tester"
        title="Regex tester"
        description="Live JavaScript regex testing with capture groups, flags, and inline highlighting."
        badges={
          <Badge variant={result.err ? 'danger' : 'success'}>
            {result.err ? 'invalid' : `${result.matches.length} match${result.matches.length === 1 ? '' : 'es'}`}
          </Badge>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <Field
              label="Pattern"
              hint={result.err ? <span className="text-danger">{result.err}</span> : null}
            >
              <div className="flex items-center gap-1 font-mono">
                <span className="text-fg-subtle">/</span>
                <Input
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  className="font-mono flex-1"
                  invalid={!!result.err}
                />
                <span className="text-fg-subtle">/{Object.entries(flags).filter(([, v]) => v).map(([k]) => k).join('')}</span>
              </div>
            </Field>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {(['g', 'i', 'm', 's'] as const).map((f) => (
                <label
                  key={f}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 rounded-md border cursor-pointer transition',
                    flags[f] ? 'border-accent bg-accent/5' : 'border-border hover:border-border-strong',
                  )}
                >
                  <div className="text-xs">
                    <div className="font-mono font-semibold">{f}</div>
                    <div className="text-2xs text-fg-subtle">{FLAG_LABELS[f]}</div>
                  </div>
                  <Switch checked={flags[f]} onChange={(v) => setFlags((p) => ({ ...p, [f]: v }))} />
                </label>
              ))}
            </div>
          </Card>

          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Test string
            </div>
            <Textarea
              mono
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0"
              placeholder="Paste your test text…"
            />
          </Card>

          <Card>
            <div className="text-xs font-medium text-fg-muted mb-2">Highlighted</div>
            <div
              className="font-mono text-sm leading-relaxed whitespace-pre-wrap break-words"
              dangerouslySetInnerHTML={{ __html: highlighted }}
            />
          </Card>
        </div>

        {/* Matches sidebar */}
        <Card className="!p-0 lg:sticky lg:top-4 self-start">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <div className="text-xs font-medium text-fg-muted">Matches</div>
            <span className="text-2xs text-fg-subtle font-mono flex items-center gap-1">
              <Hash size={10} />
              {result.matches.length}
            </span>
          </div>
          <div className="max-h-[440px] overflow-y-auto divide-y divide-border">
            {result.err && (
              <div className="p-4 text-xs flex items-start gap-2 text-danger">
                <AlertCircle size={12} className="shrink-0 mt-0.5" />
                <span>{result.err}</span>
              </div>
            )}
            {!result.err && result.matches.length === 0 && (
              <div className="p-4 text-xs text-fg-subtle text-center">No matches</div>
            )}
            {result.matches.map((m, i) => (
              <div key={i} className="p-3 text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-medium text-accent">match #{i + 1}</span>
                  <span className="text-2xs text-fg-subtle">at {m.index}</span>
                </div>
                <div className="font-mono break-all">{m.full}</div>
                {m.groups.length > 0 && (
                  <div className="space-y-1 mt-2 pl-2 border-l-2 border-accent/40">
                    {m.groups.map((g, j) => (
                      <div key={j} className="font-mono">
                        <span className="text-fg-subtle">${j + 1}:</span>{' '}
                        <span className="text-fg">{g ?? '∅'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const FLAG_LABELS: Record<string, string> = {
  g: 'global',
  i: 'ignore case',
  m: 'multiline',
  s: 'dot all',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c]!);
}

export default RegexTester;
